import { type DeepPartial, type Module, inject } from "langium";
import {
	type DefaultSharedModuleContext,
	type LangiumServices,
	type LangiumSharedServices,
	type PartialLangiumServices,
	createDefaultModule,
	createDefaultSharedModule,
} from "langium/lsp";
import { IprotoGeneratedModule, IprotoGeneratedSharedModule } from "./generated/module.js";
import { IprotoCompletionProvider } from "./lsp/completion.js";
import { IprotoFormatter } from "./lsp/formatter.js";
import { IprotoHoverProvider } from "./lsp/hover.js";
import { IprotoSemanticTokenProvider } from "./lsp/semantic-token.js";
import { IprotoScopeComputation } from "./reference/scope-computation.js";
import { IprotoScopeProvider } from "./reference/scope-provider.js";
import { IprotoValidator } from "./validator/validator.js";
import { IprotoWorkspaceManager } from "./workspace/workspace-manager.js";

export type IprotoAddedServices = {
	validation: {
		IprotoValidator: IprotoValidator;
	};
};
export type IprotoServices = LangiumServices & IprotoAddedServices;
export type IprotoSharedServices = LangiumSharedServices;

const IprotoModule: Module<IprotoServices, PartialLangiumServices & IprotoAddedServices> = {
	validation: {
		IprotoValidator: (services) => new IprotoValidator(services),
	},
	references: {
		ScopeComputation: (services) => new IprotoScopeComputation(services),
		ScopeProvider: (services) => new IprotoScopeProvider(services),
	},
	lsp: {
		SemanticTokenProvider: (services) => new IprotoSemanticTokenProvider(services),
		HoverProvider: (services) => new IprotoHoverProvider(services),
		CompletionProvider: (services) => new IprotoCompletionProvider(services),
		Formatter: (services) => new IprotoFormatter(services),
	},
};

const IprotoSharedModule: Module<IprotoSharedServices, DeepPartial<IprotoSharedServices>> = {
	workspace: {
		WorkspaceManager: (services) => new IprotoWorkspaceManager(services),
	},
};

export class IprotoModuleBuilder {
	services: IprotoServices;
	sharedServices: LangiumSharedServices;

	constructor(context: DefaultSharedModuleContext) {
		const defaultSharedServices = createDefaultSharedModule(context);
		const sharedServices = inject(defaultSharedServices, ...this.getCustomSharedServices());
		const defaultServices = createDefaultModule({ shared: sharedServices });
		const services = inject(defaultServices, ...this.getCustomServices());

		this.services = services;
		this.sharedServices = sharedServices;

		this.register();

		if (!context.connection) {
			sharedServices.workspace.ConfigurationProvider.initialized({});
		}
	}

	private getCustomServices() {
		return [IprotoGeneratedModule, IprotoModule] as const;
	}

	private getCustomSharedServices() {
		return [IprotoGeneratedSharedModule, IprotoSharedModule] as const;
	}

	private register() {
		this.sharedServices.ServiceRegistry.register(this.services);
		this.services.validation.IprotoValidator.register(this.services);
	}
}
